/**
 * oceanContainerWeight.js — weight math for ocean container rows.
 *
 * The Equipment modal asks for a weight plus a weight type (the user must pick
 * one — there is no default):
 *   weightType "unit"  — the typed weight is for ONE container (label KG/UNIT)
 *   weightType "total" — the typed weight is for the whole line (label TOTAL)
 * `weightunit` is always "KG" (the unit of measure, same for both types).
 *
 * Ocean weights are whole KG: every typed weight is rounded to the nearest KG
 * (.5 and above rounds up — 50.60 → 51, 40.49 → 40) BEFORE any multiplying,
 * so a KG/UNIT line of 5 × 17.30 is 17 × 5 = 85. The backend applies the same
 * rule (freightsee-backend utils/oceanContainerWeight.js).
 *
 * In the search payload (`containerDetails`) `kgs` is ALWAYS the line's total
 * weight: "unit" rows are sent as rounded weight × quantity, "total" rows as
 * the rounded weight. Every view and the rate search use `kgs`.
 * `enteredKgs` carries the weight exactly as typed (e.g. "200.60") only so
 * the modal can show it again on Edit Query / booking update — it is never
 * used for any calculation. The modal's own draft (`GoodDetails.containers`)
 * also keeps the typed value; use toPayloadContainer / toModalRow to convert.
 *
 * Older row shapes still read correctly:
 *   - no weightType, weightunit "KG/UNIT" → "unit"  (kgs is the line total)
 *   - no weightType, weightunit "KG"      → "total" (kgs is the line total)
 *   - neither key (pre-dropdown rows)     → kgs is the weight of ONE container
 */

export const OCEAN_WEIGHT_TYPE_UNIT = "unit";
export const OCEAN_WEIGHT_TYPE_TOTAL = "total";
export const OCEAN_WEIGHT_UNIT = "KG";

// Same labels as air's "Select wt unit" dropdown.
export const OCEAN_WEIGHT_TYPE_OPTIONS = [
  { value: OCEAN_WEIGHT_TYPE_UNIT, label: "KG/UNIT" },
  { value: OCEAN_WEIGHT_TYPE_TOTAL, label: "TOTAL" },
];

const qtyOf = (c) => Number(c?.quantity ?? c?.qty) || 1;

/** Nearest whole KG, .5 rounding up (50.60 → 51, 40.49 → 40). 0 when blank. */
export const roundOceanWeight = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

/**
 * The row's weight type, or null for a pre-dropdown row whose kgs is the
 * weight of one container.
 */
export const getWeightType = (c) => {
  const wt = String(c?.weightType || "").toLowerCase();
  if (wt === OCEAN_WEIGHT_TYPE_UNIT || wt === OCEAN_WEIGHT_TYPE_TOTAL) return wt;
  if (c?.weightunit === "KG/UNIT") return OCEAN_WEIGHT_TYPE_UNIT;
  if (c?.weightunit === "KG") return OCEAN_WEIGHT_TYPE_TOTAL;
  return null;
};

/** Payload row → weight (KG) of one container on the line, whole KG. */
export const containerUnitWeight = (c) => {
  const kgs = parseFloat(c?.kgs) || 0;
  // Typed rows carry the line total; pre-dropdown rows are per container.
  return roundOceanWeight(getWeightType(c) ? kgs / qtyOf(c) : kgs);
};

/** Payload row → total weight (KG) of the line, whole KG. */
export const containerLineWeight = (c) => {
  // "total" is rounded as a whole; "unit" / pre-dropdown rows are rounded per
  // container first and then multiplied, so rows saved before rounding
  // existed come out the same as new ones.
  if (getWeightType(c) === OCEAN_WEIGHT_TYPE_TOTAL) return roundOceanWeight(c?.kgs);
  return containerUnitWeight(c) * qtyOf(c);
};

/** Gross weight (KG) across every payload container line. */
export const totalContainerWeight = (containers) =>
  (Array.isArray(containers) ? containers : []).reduce(
    (sum, c) => sum + containerLineWeight(c),
    0
  );

/**
 * Modal row (typed weight) → payload row, where `kgs` is the line total:
 * the typed weight is rounded first, then "unit" multiplies by quantity and
 * "total" is sent as is.
 */
export const toPayloadContainer = (r) => {
  const typed = roundOceanWeight(r?.kgs);
  const weightType = getWeightType(r) || OCEAN_WEIGHT_TYPE_UNIT;
  const kgs = weightType === OCEAN_WEIGHT_TYPE_TOTAL ? typed : typed * qtyOf(r);
  return {
    type: r?.type,
    quantity: String(r?.quantity),
    kgs: String(kgs),
    // As typed, for re-opening the modal only (never calculated with).
    enteredKgs: String(r?.kgs ?? ""),
    weightunit: OCEAN_WEIGHT_UNIT,
    weightType,
  };
};

/**
 * Any payload-shaped row → the current payload shape
 * { type, quantity, kgs (line total, whole KG), weightunit: "KG", weightType }.
 * Covers rows that reach the search without passing through the modal (Edit
 * Query / booking update on an older query): pre-dropdown rows are per
 * container, so they are multiplied out and become "unit". Any other keys on
 * the row are kept.
 */
export const normalizePayloadContainer = (c) => {
  if (!c || typeof c !== "object") return c;
  const weightType = getWeightType(c) || OCEAN_WEIGHT_TYPE_UNIT;
  // No kgs at all → nothing to round; still stamp the type/unit.
  const hasKgs = c.kgs != null && c.kgs !== "";
  return {
    ...c,
    ...(hasKgs ? { kgs: String(containerLineWeight(c)) } : {}),
    weightunit: OCEAN_WEIGHT_UNIT,
    weightType,
  };
};

/**
 * Payload row → modal row, recovering the weight the user typed: enteredKgs
 * when the row has it, otherwise (rows saved before it existed) the rounded
 * weight worked back from kgs.
 */
export const toModalRow = (c) => {
  const weightType = getWeightType(c);
  const hasKgs = c?.kgs != null && c?.kgs !== "";
  const hasEntered = c?.enteredKgs != null && c?.enteredKgs !== "";
  // Pre-dropdown rows: kgs was always the weight of one container.
  const kgs = hasEntered
    ? String(c.enteredKgs)
    : !hasKgs
    ? c?.kgs
    : weightType === OCEAN_WEIGHT_TYPE_TOTAL
    ? String(roundOceanWeight(c.kgs))
    : String(containerUnitWeight(c));
  return {
    ...c,
    kgs,
    weightunit: OCEAN_WEIGHT_UNIT,
    weightType: weightType || OCEAN_WEIGHT_TYPE_UNIT,
  };
};

/** Display text for a payload row, e.g. "1000 KG (500 KG/UNIT)" / "1200 KG TOTAL". */
export const containerWeightLabel = (c) => {
  if (c?.kgs == null || c?.kgs === "") return "";
  const total = containerLineWeight(c);
  if (getWeightType(c) === OCEAN_WEIGHT_TYPE_TOTAL) return `${total} KG TOTAL`;
  return `${total} KG (${containerUnitWeight(c)} KG/UNIT)`;
};
